import { useQuery } from '@tanstack/react-query';
import { Dispatch } from 'redux';

import { requestHandler } from '../../../api';
import {
  AvailableBusinessResponse,
  FundSummary,
  PlacementsResponse,
  RelationReport,
} from './types';

const BASE_KEYS = ['multiverse', 'fund'];

export const getAvailableBusinessForFund = (dispatch: Dispatch) =>
  useQuery({
    queryKey: [...BASE_KEYS, 'availableBusiness'],
    queryFn: async (): Promise<AvailableBusinessResponse | undefined> => {
      const response = await requestHandler({
        method: 'get',
        url: `internal/multiverse/fund/shared`,
        dispatch,
      });
      if (response) return response.data;
    },
    staleTime: 1000 * 60 * 60,
    gcTime: 1000 * 60 * 60 * 24,
    retry: 1,
  });

export const getSharedBusinessDataHubLightReport = async (
  dispatch: Dispatch,
  businessIdentifier: string,
  companyId: number,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/multiverse/fund/${companyId}/${businessIdentifier}/datahubLightReport/${businessIdentifier}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getSharedBusinessPlacements = (
  dispatch: Dispatch,
  businessIdentifier: string,
  companyId: string,
  query?: string,
) =>
  useQuery({
    queryKey: [
      ...BASE_KEYS,
      'placements',
      businessIdentifier,
      companyId,
      ...(query ? [query] : []),
    ],
    queryFn: async (): Promise<PlacementsResponse | undefined> => {
      const queryString = query ? `?${query}` : '';
      const url = `internal/multiverse/fund/${companyId}/${businessIdentifier}/placements${queryString}`;
      const response = await requestHandler({
        method: 'get',
        url,
        dispatch,
      });
      if (response) return response.data;
    },
    staleTime: 1000 * 60 * 60,
    gcTime: 1000 * 60 * 60 * 24,
    retry: 1,
  });

export const getSharedBusinessReport = (
  dispatch: Dispatch,
  businessIdentifier: string,
  companyId: string,
  query?: string,
) =>
  useQuery({
    queryKey: [
      ...BASE_KEYS,
      'report',
      businessIdentifier,
      companyId,
      ...(query ? [query] : []),
    ],
    queryFn: async (): Promise<RelationReport | undefined> => {
      const queryString = query ? `?${query}` : '';
      const url = `internal/multiverse/fund/${companyId}/${businessIdentifier}/report${queryString}`;
      const response = await requestHandler({
        method: 'get',
        url,
        dispatch,
      });
      if (response) return response.data;
    },
    staleTime: 1000 * 60 * 60,
    gcTime: 1000 * 60 * 60 * 24,
    retry: 1,
  });

export const getFundSummary = (dispatch: Dispatch) =>
  useQuery({
    queryKey: [...BASE_KEYS, 'summary'],
    queryFn: async (): Promise<FundSummary | undefined> => {
      const response = await requestHandler({
        method: 'get',
        url: `internal/multiverse/fund/multiTenant/summary`,
        dispatch,
      });
      if (response) return response.data;
    },
    staleTime: 1000 * 60 * 60,
    gcTime: 1000 * 60 * 60 * 24,
    retry: 1,
  });

export const createSharedCreditLineEvaluation = async (
  dispatch: Dispatch,
  payload: object,
  businessIdentifier: string,
  companyId: number,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/multiverse/fund/${companyId}/${businessIdentifier}/decisionEngine/creditLine`,
    data: payload,
    dispatch,
  });
  if (response) return response.data;
};
